import React from "react";
import { Instagram } from "lucide-react";
import { useI18n } from "../i18n";
import { INSTAGRAM_URL, WHATSAPP_URL } from "../constants";

export default function Footer() {
  const { t, lang } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer
      className="bg-[#070605] border-t border-[#2d2622] pt-16 pb-10"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pb-12 border-b border-[#2d2622]">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3">
              <span className="tinta-badge">T.</span>
              <div className="leading-none">
                <div className="font-serif-display text-xl tracking-wide text-[#f4f0eb]">
                  TINTA
                </div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[#a39e97] mt-0.5">
                  Restaurante & Bar
                </div>
              </div>
            </div>
            <p className="mt-5 text-[#a39e97] text-sm max-w-sm leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Nav */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37] mb-4">
              {t.footer.nav_title}
            </div>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#about" className="text-[#d9d4cd] hover:text-[#d4af37] transition-colors">
                  {t.nav.about}
                </a>
              </li>
              <li>
                <a href="#menu" className="text-[#d9d4cd] hover:text-[#d4af37] transition-colors">
                  {t.nav.menu}
                </a>
              </li>
              <li>
                <a href="#gallery" className="text-[#d9d4cd] hover:text-[#d4af37] transition-colors">
                  {t.nav.gallery}
                </a>
              </li>
              <li>
                <a href="#contact" className="text-[#d9d4cd] hover:text-[#d4af37] transition-colors">
                  {t.nav.contact}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#d4af37] mb-4">
              {t.footer.contact_title}
            </div>
            <ul className="space-y-2 text-sm text-[#d9d4cd]">
              <li>José A. Cabrera 5621</li>
              <li>Palermo, CABA</li>
              <li>
                <a href="tel:+541158214406" className="hover:text-[#d4af37] transition-colors">
                  011 5821-4406
                </a>
              </li>
              <li className="pt-2 flex gap-3">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 flex items-center justify-center border border-[#2d2622] text-[#d9d4cd] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors"
                  data-testid="footer-instagram-link"
                >
                  <Instagram size={16} />
                </a>
                <a
                  href={WHATSAPP_URL(
                    lang === "es"
                      ? "Hola TINTA, tengo una consulta."
                      : "Hi TINTA, I have a question."
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="w-9 h-9 flex items-center justify-center border border-[#2d2622] text-[#d9d4cd] hover:text-[#25D366] hover:border-[#25D366] transition-colors"
                  data-testid="footer-whatsapp-link"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-[#a39e97]">
          <div>
            © {year} TINTA Restaurante & Bar · {t.footer.rights}
          </div>
          <div className="uppercase tracking-[0.28em]">
            Palermo · Buenos Aires · Argentina
          </div>
        </div>
      </div>
    </footer>
  );
}
